/*
 * <swatch-row> — scroll affordance for the colourway strip.
 *
 * The strip scrolls rather than wraps (65 PDPs carry five or more colourways),
 * and overflow alone is invisible: nothing tells you there is more to the right.
 * These are the cart cross-sell's arrows, mirrored rather than shared — that
 * handler is hard-scoped to .cart-drawer__cross-sell selectors, and coupling the
 * PDP to a cart asset to save thirty lines is the worse trade.
 *
 * The controls are hidden unless the strip actually overflows, so a two-colourway
 * product gets no dead arrows.
 *
 * It also drives the caption under the strip (current style at rest, hovered or
 * focused while pointing) and reports a tile click to the dataLayer. Not
 * CSS-only: :has() can't say WHICH of N siblings is hovered.
 *
 * And it carries the chosen size across to each sibling's href. Liquid can't
 * bake that in: on an organic landing the preselect gate leaves no size chosen,
 * so a server-rendered ?variant= would encode the size the shopper didn't pick.
 */

class SwatchRow extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('[data-swatch-scroller]');
    if (!this.scroller) return;

    this.prefills = Array.from(this.scroller.querySelectorAll('.swatch-img[data-sizes]')).map((tile) => ({
      tile,
      base: tile.getAttribute('href'),
      sizes: SwatchRow.parseSizes(tile.dataset.sizes),
    }));
    if (this.prefills.length) {
      /* Bound on document, not on the picker: the change bubbles past
         VariantSelects' own handler, and the picker may not exist yet when a
         deferred swatch-row.js upgrades this element. */
      this.onSizeChange = this.syncPrefill.bind(this);
      document.addEventListener('change', this.onSizeChange);
      this.syncPrefill();
    }

    this.controls = this.querySelector('[data-swatch-controls]');
    if (!this.controls) return;

    this.prev = this.controls.querySelector('[data-scroll="prev"]');
    this.next = this.controls.querySelector('[data-scroll="next"]');

    this.onClick = this.handleClick.bind(this);
    this.controls.addEventListener('click', this.onClick);

    this.onTileClick = this.handleTileClick.bind(this);
    this.scroller.addEventListener('click', this.onTileClick);

    /* Middle-click never fires 'click', so auxclick catches it separately. */
    this.onTileAuxClick = this.handleTileAuxClick.bind(this);
    this.scroller.addEventListener('auxclick', this.onTileAuxClick);

    this.caption = this.querySelector('[data-swatch-caption]');
    this.currentStyle = this.dataset.currentStyle;
    if (this.caption) {
      /* pointerenter doesn't bubble, so pointerover/pointerout + closest() is
         the delegatable pair. Keyboard gets the same swap unconditionally
         since focus reaches these tiles on touch too. */
      this.onTileIn = this.showStyle.bind(this);
      this.onTileOut = this.restoreStyle.bind(this);
      this.scroller.addEventListener('focusin', this.onTileIn);
      this.scroller.addEventListener('focusout', this.onTileOut);

      /* The same gate the arrows use, one step stricter. Without it iOS treats
         the first tap as a hover and the second as the click. */
      this.hasHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (this.hasHover) {
        this.scroller.addEventListener('pointerover', this.onTileIn);
        this.scroller.addEventListener('pointerout', this.onTileOut);
      }
    }

    /* component-swatch-row.css hides the arrows under (pointer: coarse), so on
       touch every swipe frame would pay three forced layout reads for output
       nobody sees. The controls ship hidden, which is the right resting state. */
    if (window.matchMedia('(pointer: coarse)').matches) return;

    this.onScroll = this.syncArrows.bind(this);
    this.measure = this.measure.bind(this);

    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

    /* Tiles carry explicit dimensions in CSS, so the strip's scrollWidth is
       already correct here and does not wait on image decode. */
    this.resizeObserver = new ResizeObserver(this.measure);
    this.resizeObserver.observe(this.scroller);

    this.measure();
  }

  disconnectedCallback() {
    if (this.onSizeChange) document.removeEventListener('change', this.onSizeChange);
    if (this.controls) this.controls.removeEventListener('click', this.onClick);
    if (this.scroller && this.onScroll) this.scroller.removeEventListener('scroll', this.onScroll);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (!this.scroller) return;

    if (this.onTileClick) this.scroller.removeEventListener('click', this.onTileClick);
    if (this.onTileAuxClick) this.scroller.removeEventListener('auxclick', this.onTileAuxClick);
    if (this.onTileIn) {
      this.scroller.removeEventListener('focusin', this.onTileIn);
      this.scroller.removeEventListener('focusout', this.onTileOut);
      if (this.hasHover) {
        this.scroller.removeEventListener('pointerover', this.onTileIn);
        this.scroller.removeEventListener('pointerout', this.onTileOut);
      }
    }
  }

  /* Split on the LAST colon so a size string carrying one still resolves. */
  static parseSizes(value) {
    const map = new Map();
    (value || '').split('|').forEach((entry) => {
      const cut = entry.lastIndexOf(':');
      if (cut > 0) map.set(entry.slice(0, cut), entry.slice(cut + 1));
    });
    return map;
  }

  /* The native select, not the chip grid: it is the buy path's source of truth
     and it is what reads blank while the preselect gate is holding. Scoped by
     product id, not just DOM order — a complementary block's quick-add modal
     nests its own <size-picker> for a different product inside the same
     #MainProduct- root. No match at any step (no section root, no fieldset for
     this id, id renders outside a <size-picker>) — fail closed rather than
     guess via document or :has(), which throws in pre-2023 browsers. */
  selectedSize() {
    const scope = this.closest('[id^="MainProduct-"], [id^="QuickAddInfo-"]');
    if (!scope) return '';
    const fieldset = scope.querySelector(`[data-size-picker][data-product-id="${this.dataset.productId}"]`);
    const picker = fieldset ? fieldset.closest('size-picker') : null;
    const select = picker ? picker.querySelector('.product-form__input--dropdown select') : null;
    return select ? select.value : '';
  }

  syncPrefill() {
    const size = this.selectedSize();

    this.prefills.forEach((entry) => {
      const variantId = size ? entry.sizes.get(size) : undefined;
      entry.tile.setAttribute('href', variantId ? `${entry.base}?variant=${variantId}` : entry.base);
    });
  }

  showStyle(event) {
    const tile = event.target.closest('.swatch-img');
    if (!tile) return;

    this.caption.textContent = tile.dataset.style;
  }

  restoreStyle(event) {
    const tile = event.target.closest('.swatch-img');
    if (!tile) return;

    /* Crossing from the tile's link to its own image fires pointerout before
       the matching pointerover, and restoring on that would flicker the caption
       back to the current style mid-hover. */
    const entering = event.relatedTarget;
    if (entering && tile.contains(entering)) return;

    /* Mouse can leave the strip while Tab focus still sits on another tile —
       the caption should keep naming that tile, not fall back early. */
    if (event.type === 'pointerout') {
      const focusedTile = this.scroller.contains(document.activeElement)
        ? document.activeElement.closest('.swatch-img')
        : null;
      if (focusedTile) {
        this.caption.textContent = focusedTile.dataset.style;
        return;
      }
    }

    /* Mirror of the pointerout branch: Tab focus can leave a tile while the
       mouse still hovers another one, so the caption should keep naming
       that tile rather than falling back early. */
    if (event.type === 'focusout' && this.hasHover) {
      const hoveredTile = this.scroller.querySelector('.swatch-img:hover');
      if (hoveredTile) {
        this.caption.textContent = hoveredTile.dataset.style;
        return;
      }
    }

    this.caption.textContent = this.currentStyle;
  }

  /* Custom-named, like pdp_size_selected — GA4's select_item already fires
     from checkout_datalayer. The current tile has no target id, so its own
     link would otherwise reload the page: preventDefault stops that for a
     plain click, but a modifier click (Cmd/Ctrl/Shift/Alt) should still open
     it like any other link. */
  handleTileClick(event) {
    const tile = event.target.closest('.swatch-img');
    if (!tile) return;

    if (!tile.dataset.targetProductId) {
      if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
      }
      return;
    }

    this.pushStyleSelected(tile);
  }

  handleTileAuxClick(event) {
    if (event.button !== 1) return;

    const tile = event.target.closest('.swatch-img');
    if (!tile || !tile.dataset.targetProductId) return;

    this.pushStyleSelected(tile);
  }

  pushStyleSelected(tile) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pdp_style_selected',
      product_id: this.dataset.productId,
      target_product_id: tile.dataset.targetProductId,
      style: tile.dataset.style,
      available: tile.dataset.available === 'true',
      /* Read off the href rather than recomputed, so it is exactly what the
         click navigates to. Empty when nothing was prefilled. */
      variant_id: new URL(tile.href).searchParams.get('variant') || '',
    });
  }

  /* Both layout-flushing reads live here rather than in the scroll handler: the
     strip's scrollable extent only changes when its box does, and the observer
     already fires on exactly that. */
  measure() {
    this.maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
    this.controls.hidden = this.maxScroll <= 1;
    this.syncArrows();
  }

  syncArrows() {
    if (this.controls.hidden) return;

    const left = this.scroller.scrollLeft;
    this.prev.disabled = left <= 1;
    this.next.disabled = left >= this.maxScroll - 1;
  }

  handleClick(event) {
    const arrow = event.target.closest('[data-scroll]');
    if (!arrow) return;

    const tile = this.scroller.querySelector('.swatch-img');
    const gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
    const pitch = tile ? tile.getBoundingClientRect().width + gap : this.scroller.clientWidth;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    this.scroller.scrollBy({ left: arrow.dataset.scroll === 'prev' ? -pitch : pitch, behavior });
  }
}

if (!customElements.get('swatch-row')) {
  customElements.define('swatch-row', SwatchRow);
}
